import { useState, createContext } from "react";
import genericStyles from "../../Styles/genericStyles.module.css";
import "./OnlineClientsTable.module.css";
import MUIDataTableCustomStyle from "../../Components/MUIDataTableCustomStyle";
import useCustomMemo from "../../Hooks/useCustomMemo";
import { Vocabulary } from "../../Utils/Vocabulary";
import { endpoints, localUrlEnum } from "../../Utils/UrlEnum";
import { OrderModel } from "../../Utils/Models";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { ETableEditAction } from "../../Utils/Enums";
import withRole from "../../Hooks/withRole";
import { updateData as updateDataService } from "../../Services/updateData";
import { getData } from "../../Services/getData";
import { getOnlineClientsColumns } from "./OnlineClientsTableColumns";
import { OrderSource } from "../../Utils/Constants";

export const OnlineClientsCRUDContext = createContext(new OrderModel());

function OnlineClientsTable(props: any) {
  const [contextValue, setContextValue] = useState(new OrderModel());
  const [triggerReload, setTriggerReload] = useState<undefined | boolean>();
  const [commentModal, setCommentModal] = useState<{
    open: boolean;
    orderId: string | null;
    currentComment: string;
  }>({
    open: false,
    orderId: null,
    currentComment: "",
  });
  const memo = useCustomMemo();

  // Reusable function to update order
  const updateOrder = async (orderId: string, updateData: any) => {
    try {
      await updateDataService(
        `${endpoints.ordersEndpoint}/${orderId}`,
        updateData
      );
      setTriggerReload(!triggerReload);
    } catch (error) {
      console.error("Error updating order:", error);
    }
  };

  const processOrder = async (orderId: string) => {
    // Fetch the order to get product information
    const orderResponse = await getData(`${endpoints.ordersEndpoint}/${orderId}`);
    const order = orderResponse?.data;
    
    // Determine order type based on product
    const isCertificate = order?.product?.isCertificate;
    const orderTypeName = isCertificate ? "Atestat" : "Permis";
    
    // Get all statuses from storage
    const allStatuses = memo?.storage?.orderStatuses || [];
    
    // Filter statuses by order type
    const typeStatuses = allStatuses.filter(
      (status: any) => status?.typeId?.name === orderTypeName
    );
    
    // Find the first status (lowest sortNumber)
    const firstStatus = typeStatuses.reduce((prev: any, current: any) => {
      if (!prev) return current;
      return (current.sortNumber ?? Infinity) < (prev.sortNumber ?? Infinity)
        ? current
        : prev;
    }, null);

    await updateOrder(orderId, {
      isProcessed: true,
      seenAt: new Date().toISOString(),
      status: firstStatus?._id || null,
    });
  };

  /**
   * Marks an order as seen.
   *
   * @param {string} orderId - The id of the order.
   */
  const markOrderAsSeen = async (orderId: string) => {
    await updateOrder(orderId, {
      seenAt: new Date().toISOString(),
    });
  };

  /**
   * Handles row click event by marking the order as seen and navigating to the order details in a new window.
   * @param {any} rowData - The data of the row that was clicked.
   */
  const handleRowClick = (rowData: any) => {
    const orderId = rowData?._id; // _id

    // Mark as seen if not already seen
    if (!rowData?.seenAt) markOrderAsSeen(orderId);

    // Navigate to order details in new window
    window.open(`${localUrlEnum.order}/${orderId}`, "_blank");
  };

  /**
   * Saves the comment for the order specified in the commentModal state.
   * If there is no orderId in the commentModal state, the comment is not saved.
   */
  const handleCommentSave = async () => {
    if (commentModal.orderId) {
      await updateOrder(commentModal.orderId, {
        comments: commentModal.currentComment,
        seenAt: new Date().toISOString(),
      });
    }
    setCommentModal({ open: false, orderId: null, currentComment: "" });
  };

  // Get columns with callback functions
  const onlineClientsColumns = getOnlineClientsColumns(
    processOrder,
    setCommentModal
  );

  return (
    <div className={genericStyles.mainContainer}>
      <OnlineClientsCRUDContext.Provider value={contextValue}>
        <MUIDataTableCustomStyle
          onAddAction={ETableEditAction.REDIRECT}
          onEditAction={ETableEditAction.MODAL}
          title={Vocabulary.onlineClients}
          columns={onlineClientsColumns}
          shouldTriggerStorageUpdate={true}
          CRUDEndPoint={endpoints.ordersEndpoint}
          id="onlineClientsTable"
          setContextValue={setContextValue}
          triggerReload={triggerReload}
          hideAddButton={true}
          hideEditButton={true}
          hideDeleteButton={true}
          onRowClick={handleRowClick}
          filters={{ source: OrderSource.ONLINE }}
          showPrintButton
          showDownloadButton
          exportWithTimestamp
          options={{
            setRowProps: (row: any, _: number, rowIndex: number) => {
              const isUnseen = !row[1]; // seen status
              return {
                className: isUnseen ? "unseen-order-row" : "",
                style: {
                  cursor: "pointer",
                  backgroundColor: isUnseen
                    ? undefined
                    : rowIndex % 2
                    ? "#fff"
                    : "#ebeded",
                },
              };
            },
          }}
        />

        {/* Comment Modal */}
        <Dialog
          open={commentModal.open}
          onClose={() =>
            setCommentModal({ open: false, orderId: null, currentComment: "" })
          }
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "16px",
              boxShadow:
                "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            },
          }}
        >
          <DialogTitle
            sx={{
              pb: 1,
              fontSize: "1.25rem",
              fontWeight: 600,
              color: "primary.main",
            }}
          >
            Adaugă comentariu
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              label="Comentariu"
              value={commentModal.currentComment}
              onChange={(e) =>
                setCommentModal((prev) => ({
                  ...prev,
                  currentComment: e.target.value,
                }))
              }
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                },
              }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button
              onClick={() =>
                setCommentModal({
                  open: false,
                  orderId: null,
                  currentComment: "",
                })
              }
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 500,
              }}
            >
              {Vocabulary.cancel}
            </Button>
            <Button
              onClick={handleCommentSave}
              variant="contained"
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 500,
                backgroundColor: "primary.main",
              }}
            >
              {Vocabulary.save}
            </Button>
          </DialogActions>
        </Dialog>
      </OnlineClientsCRUDContext.Provider>
    </div>
  );
}

export default withRole(OnlineClientsTable);
